import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { nord } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'katex/dist/katex.min.css';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

const Message = ({ message }) => {
  const { role, content, created_at, conversation_round_num } = message;
  const isUser = role === 'user';
  const isTool = role === 'tool';
  const isSystem = role === 'system';
  const [showRaw, setShowRaw] = useState(false);

  // Format date
  const formattedDate = new Date(created_at).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  // Function to copy raw content
  const copyRawContent = () => {
    navigator.clipboard.writeText(content);
    // You could add a toast notification here
  };

  // Determine background color and label based on role
  const getMessageStyles = () => {
    if (isUser) return { bgColor: 'message-user ml-12', avatar: 'bg-primary-600', label: 'You', symbol: 'U' };
    if (isTool) return { bgColor: 'message-tool ml-6 mr-6 bg-gray-50', avatar: 'bg-gray-600', label: 'Tool', symbol: 'T' };
    if (isSystem) return { bgColor: 'message-system mx-12 bg-indigo-50 border border-indigo-200', avatar: 'bg-indigo-600', label: 'System', symbol: 'S' };
    return { bgColor: 'message-assistant mr-12', avatar: 'bg-secondary-600', label: 'Assistant', symbol: 'AI' };
  };

  const { bgColor, avatar, label, symbol } = getMessageStyles();

  return (
    <div className={`p-4 mb-4 rounded-lg ${bgColor}`}>
      <div className="flex items-center mb-2">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${avatar}`}>
          {symbol}
        </div>
        <span className="ml-2 font-medium">{label}</span>
        <span className="ml-2 text-xs text-gray-500">Round {conversation_round_num || 0}</span>
        <span className="ml-auto text-xs text-gray-500">{formattedDate}</span>
        
        {!isUser && (
          <div className="flex ml-2">
            <button
              onClick={() => setShowRaw(!showRaw)}
              className="text-gray-500 hover:text-gray-700 p-1 text-xs"
              title={showRaw ? "Show formatted" : "Show raw markdown"}
            >
              {showRaw ? "Format" : "Raw"}
            </button>
            <button
              onClick={copyRawContent}
              className="text-gray-500 hover:text-gray-700 p-1 text-xs ml-1"
              title="Copy raw content"
            >
              Copy
            </button>
          </div>
        )}
      </div>

      <div className="prose prose-sm w-full overflow-hidden break-words">
        {showRaw ? (
          <pre className="bg-gray-100 p-2 rounded-md overflow-x-auto">
            {content}
          </pre>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={nord}
                    language={match[1]}
                    PreTag="div"
                    customStyle={{ overflow: 'auto', wordWrap: 'break-word' }}
                    {...props}
                  >
                    {String(children).replace(/\\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {content}
          </ReactMarkdown>
        )}
      </div>
    </div>
  );
};

export default Message;